<script setup lang="ts">
import { IonModal } from '@ionic/vue';
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ConsentPurpose } from '@/api/user/types/privacy-consents';
import PrivacyConsentRow from './privacy-consent-row.vue';

const props = defineProps<{ isOpen: boolean; purposes: ConsentPurpose[]; showIosNote?: boolean; busy?: boolean }>();
const emit = defineEmits<{ continue: [choices: Partial<Record<ConsentPurpose, boolean>>] }>();

const { t } = useI18n();
// Every purpose starts switched off each time the sheet opens.
const choices = reactive<Partial<Record<ConsentPurpose, boolean>>>({});
watch(
  () => props.isOpen,
  open => {
    if (!open) return;
    for (const purpose of props.purposes) choices[purpose] = false;
  },
  { immediate: true }
);

// Grants every purpose offered on this sheet (and shows it), then continues.
const acceptAll = () => {
  for (const purpose of props.purposes) choices[purpose] = true;
  emit('continue', { ...choices });
};
</script>

<template>
  <!-- Optional consents: all off by default and independent. "Accept All" grants every offered
       purpose; "Continue" saves the toggles exactly as set, so continuing with all off declines.
       No backdrop dismiss, so one of the two buttons is how the choice is made. -->
  <ion-modal
    :is-open="isOpen"
    class="privacy-consent-modal"
    aria-labelledby="privacy-consent-title"
    :show-backdrop="true"
    :backdrop-dismiss="false"
  >
    <div class="privacy-consent text-tertiary" dir="ltr" data-testid="privacy-consent-sheet">
      <h3 id="privacy-consent-title" class="mb-2 mt-0 text-[22px] font-extra-bold">
        {{ t('privacy_consents.title') }}
      </h3>
      <p class="mb-1 mt-0 text-[15px]">{{ t('privacy_consents.intro') }}</p>
      <privacy-consent-row
        v-for="purpose in purposes"
        :key="purpose"
        :purpose="purpose"
        :checked="choices[purpose] === true"
        :disabled="busy"
        class="privacy-consent__row"
        @change="granted => (choices[purpose] = granted)"
      />
      <p class="mb-5 mt-3 text-[14px] text-light-medium">
        {{ t('privacy_consents.change_later') }}
        <template v-if="showIosNote && purposes.includes('metaAdMeasurement')">
          {{ t('privacy_consents.ios_note') }}
        </template>
      </p>
      <div class="flex flex-col gap-3">
        <base-button color="primary" data-testid="privacy-consent-accept-all" :disabled="busy" @click="acceptAll">
          {{ t('privacy_consents.accept_all') }}
        </base-button>
        <base-button
          color="tertiary"
          fill="outline"
          data-testid="privacy-consent-continue"
          :disabled="busy"
          @click="emit('continue', { ...choices })"
        >
          {{ t('privacy_consents.continue') }}
        </base-button>
      </div>
    </div>
  </ion-modal>
</template>

<style scoped lang="scss">
.privacy-consent {
  padding: 24px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  background: #242427;
  border-radius: 26px 26px 0 0;

  &__row + &__row {
    border-top: 1px solid #3a3a3a;
  }
}
</style>

<style lang="scss">
ion-modal.privacy-consent-modal {
  --width: 100%;
  --height: auto;
  --max-height: 100%;
  --border-radius: 26px 26px 0 0;
  --background: #242427;
  --box-shadow: none;
  --backdrop-opacity: 0.8;
  align-items: flex-end;
}
ion-modal.privacy-consent-modal::part(content) {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: auto;
  width: 100%;
  height: auto;
  max-height: 100%;
  margin: 0;
  overflow: auto;
}
</style>
